/**
 * Several orientations for one PV plant, the way PVsyst defines them.
 *
 * The browser half of pv_core/orientations.py. PVsyst's "Orientations
 * management" holds a list of planes, each a tilt and an azimuth with a name,
 * and its System dialog attaches every sub-array to one of them. The same two
 * rules hold here: every MPPT stands on an orientation that exists, and every
 * orientation has at least one MPPT. Only a fixed-tilt plant has more than one.
 *
 * How it is stored, and why:
 *
 *   * `panelOrientation` stays the editor of the PRIMARY plane. The layout, the
 *     3D scene, auto-design, the optimiser and the single-line diagram all read
 *     it, and they keep reading the same fields. The primary's entry in the list
 *     therefore carries no plane of its own - only its id and name - so the two
 *     can never disagree.
 *   * `orientations` exists only while there is more than one plane. Removing
 *     the last extra plane deletes the list and every MPPT tag, so a project
 *     that tried the feature and went back saves exactly as it did before.
 *   * A further plane's physical layout lives on its entry: `spacingInfo` in
 *     the same shape the layout tab saves for the primary on `pv_design`, and
 *     `parcel`, the ring of [lon, lat] its rows were fitted to. The engine
 *     reads the spacing; the layout tab keeps the polygon. The primary's stay
 *     where they always were (pv_design.spacingInfo, loc_info.parcel).
 *   * An MPPT on the primary carries no tag; only an MPPT on a further plane
 *     names it (`orientation: "o2"`), in `systemDesign` and in `pv_design`.
 *
 * Azimuth is the platform's compass bearing (0 = north, 90 = east, 180 =
 * south), never PVsyst's 0 = south / negative = east.
 */

export const FIELD_TYPE_FIXED = "fixed";
export const PRIMARY_ID = "o1";
const ID_RE = /^[A-Za-z0-9_-]{1,32}$/;

const num = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const out = Number(value);
  return Number.isFinite(out) ? out : null;
};

/** Python's `:g` for the angles a plane is named with: 28, 27.5, 27.25. */
export function formatAngle(value, isGreek = false) {
  const n = num(value);
  if (n === null) return "—";
  const text = String(Number(n.toPrecision(6)));
  return text;
}

/**
 * The name PVsyst gives a plane nobody named. The English form is
 * byte-identical to pv_core.orientations.auto_name, and the Greek form to the
 * rule the report localizer holds for it.
 */
export function autoName(tilt, azim, isGreek = false) {
  const t = formatAngle(tilt, isGreek);
  const a = formatAngle(azim, isGreek);
  return isGreek ? `Σταθερή, κλίση ${t}°, αζ. ${a}°` : `Fixed, tilt ${t}°, az. ${a}°`;
}

const COMPASS_EN = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const COMPASS_EL = ["Β", "ΒΑ", "Α", "ΝΑ", "Ν", "ΝΔ", "Δ", "ΒΔ"];

/** The compass point an azimuth faces, for a caption. */
export function bearing(azim, isGreek = false) {
  const a = num(azim);
  if (a === null) return "";
  const index = Math.floor((((a % 360) + 360) % 360 + 22.5) / 45) % 8;
  return (isGreek ? COMPASS_EL : COMPASS_EN)[index];
}

const isFlushRoof = (cfg) =>
  String(cfg?.panelOrientation?.installationType || "").toLowerCase() === "roof_flush";

/** The primary plane's tilt and azimuth, as the form's fields hold them. */
export function primaryPlane(cfg) {
  const po = cfg?.panelOrientation || {};
  if (isFlushRoof(cfg)) {
    return { tilt: num(po.roofPitch), azim: num(po.roofAzimuth), tiltReference: "horizontal" };
  }
  return {
    tilt: num(po.tilt),
    azim: num(po.azim),
    tiltReference: po.tiltReference === "ground" ? "ground" : "horizontal",
  };
}

/**
 * The plant's orientations, the first being the primary.
 *
 * A project without a list has exactly one: the plane in panelOrientation,
 * with the id the engine gives it ("o1"). The primary's tilt and azimuth are
 * always read from panelOrientation, whatever its stored entry says.
 */
export function orientationsFromConfig(cfg) {
  const primary = primaryPlane(cfg);
  const raw = Array.isArray(cfg?.orientations) ? cfg.orientations : [];
  if (!raw.length) {
    return [{ id: PRIMARY_ID, name: "", autoName: true, fieldType: FIELD_TYPE_FIXED, primary: true, ...primary }];
  }
  return raw.map((entry, position) => {
    const item = entry && typeof entry === "object" ? entry : {};
    const id = String(item.id || `o${position + 1}`).trim();
    const custom = item.autoName === false && String(item.name || "").trim() !== "";
    const base = {
      ...item,
      id,
      name: custom ? String(item.name).trim() : "",
      autoName: !custom,
      fieldType: item.fieldType || FIELD_TYPE_FIXED,
      primary: position === 0,
    };
    if (position === 0) return { ...base, ...primary };
    return {
      ...base,
      tilt: num(item.tilt),
      azim: num(item.azim),
      tiltReference: item.tiltReference === "ground" ? "ground" : "horizontal",
      spacingInfo: item.spacingInfo && typeof item.spacingInfo === "object" ? item.spacingInfo : null,
      parcel: Array.isArray(item.parcel) && item.parcel.length ? item.parcel : null,
      groundClearanceM: num(item.groundClearanceM),
    };
  });
}

export const isMultiOrientation = (cfg) => orientationsFromConfig(cfg).length > 1;

/** One installation boundary per unit; roof faces are edited in the 3D scene. */
export function layoutParcel(cfg) {
  if (String(cfg?.panelOrientation?.installationType || "").startsWith("roof")) return null;
  return cfg?.loc_info?.parcel || null;
}

/** What a person reads: the name they typed, or the automatic one. */
export function displayName(orientation, isGreek = false) {
  if (!orientation) return "";
  if (!orientation.autoName && orientation.name) return orientation.name;
  return autoName(orientation.tilt, orientation.azim, isGreek);
}

// ---------------------------------------------------------------- MPPTs

const mpptModules = (entry) =>
  (Array.isArray(entry?.inputs) ? entry.inputs : []).reduce((sum, input) => {
    const n = num(input?.modules_per_string ?? input?.modulesPerString);
    return sum + (n !== null && n > 0 ? n : 0);
  }, 0);

/**
 * Every MPPT of the saved design with the orientation it stands on.
 * `orientation` is resolved (untagged means the primary); `tag` is what is
 * stored, so a reference to a plane that no longer exists can be named.
 */
export function mpptAssignments(systemDesign, orientations) {
  const primaryId = orientations?.[0]?.id || PRIMARY_ID;
  const out = [];
  if (!systemDesign || typeof systemDesign !== "object") return out;
  Object.entries(systemDesign).forEach(([inverter, entries]) => {
    (Array.isArray(entries) ? entries : []).forEach((entry, position) => {
      if (!entry || typeof entry !== "object") return;
      const index = num(entry.index) ?? position + 1;
      const tag = entry.orientation == null || entry.orientation === "" ? null : String(entry.orientation).trim();
      out.push({ inverter, index, tag, orientation: tag || primaryId, modules: mpptModules(entry) });
    });
  });
  return out;
}

/** Per orientation: how many inverters, MPPTs and modules stand on it. */
export function orientationSummary(cfg) {
  const orientations = orientationsFromConfig(cfg);
  const assignments = mpptAssignments(cfg?.systemDesign, orientations);
  const total = assignments.reduce((sum, a) => sum + a.modules, 0);
  return orientations.map((o) => {
    const mine = assignments.filter((a) => a.orientation === o.id);
    const modules = mine.reduce((sum, a) => sum + a.modules, 0);
    return {
      id: o.id,
      mppts: mine.length,
      inverters: new Set(mine.map((a) => a.inverter)).size,
      modules,
      share: total > 0 ? modules / total : null,
    };
  });
}

/**
 * Every plane of the plant as a plane's layout sheet lists it: the angles, the
 * modules attached, the DC kWp they make (the plant's share by modules) and
 * whether the plane's layout has been saved.
 */
export function planesForLayoutSheet(cfg) {
  const planes = orientationsFromConfig(cfg);
  const summary = orientationSummary(cfg);
  const plantKwp = Number(cfg?.pvInstalledCap?.kw);
  return planes.map((plane, position) => {
    const mine = summary.find((s) => s.id === plane.id) || {};
    return {
      index: position + 1,
      name: !plane.autoName && plane.name ? plane.name : null,
      tilt: plane.tilt ?? null,
      azimuth: plane.azim ?? null,
      modules: mine.modules ?? 0,
      kwp: mine.share != null && plantKwp > 0 ? plantKwp * mine.share : null,
      laidOut: position === 0
        ? Boolean(cfg?.pv_design?.spacingInfo || cfg?.pv_design?.spacing)
        : Boolean(plane.spacingInfo),
    };
  });
}

// ---------------------------------------------------------------- rules

/**
 * What stops the plant from being simulated, in the order a designer fixes it.
 * The same checks as pv_core.orientations (normalize_orientations,
 * orientation_by_mppt, check_orientations), so what is red here is what the
 * run refuses. Each problem is data; problemMessage words it.
 */
export function orientationProblems(cfg) {
  const orientations = orientationsFromConfig(cfg);
  const problems = [];
  const multi = orientations.length > 1;
  const seen = new Set();
  orientations.forEach((o, position) => {
    if (!ID_RE.test(o.id)) problems.push({ code: "badId", id: o.id });
    else if (seen.has(o.id)) problems.push({ code: "duplicateId", id: o.id });
    seen.add(o.id);
    if (position > 0) {
      if (o.tilt === null || o.azim === null) problems.push({ code: "missingAngles", id: o.id });
      else if (o.tilt < 0 || o.tilt > 90) problems.push({ code: "tiltRange", id: o.id });
      else if (o.azim < 0 || o.azim > 360) problems.push({ code: "azimuthRange", id: o.id });
    }
  });
  if (multi && String(cfg?.mountType || "fixed") !== "fixed") {
    problems.push({ code: "tracker", count: orientations.length });
  }
  const assignments = mpptAssignments(cfg?.systemDesign, orientations);
  const ids = new Set(orientations.map((o) => o.id));
  assignments
    .filter((a) => a.tag && !ids.has(a.tag))
    .forEach((a) => problems.push({ code: "unknownReference", inverter: a.inverter, index: a.index, tag: a.tag }));
  if (multi && assignments.length) {
    const used = new Set(assignments.map((a) => a.orientation));
    orientations
      .filter((o) => !used.has(o.id))
      .forEach((o) => problems.push({ code: "unattached", id: o.id }));
  }
  return problems;
}

/** One sentence per problem, in the reader's language. */
export function problemMessage(problem, cfg, isGreek = false) {
  const orientation = orientationsFromConfig(cfg).find((o) => o.id === problem.id);
  const name = orientation ? displayName(orientation, isGreek) : problem.id;
  switch (problem.code) {
    case "tracker":
      return isGreek
        ? "Οι πολλαπλοί προσανατολισμοί είναι διαθέσιμοι μόνο για σταθμούς σταθερής κλίσης. Επιλέξτε σταθερή κλίση ως τύπο στήριξης ή αφαιρέστε τους επιπλέον προσανατολισμούς."
        : "Several orientations are available for fixed-tilt plants only. Choose fixed tilt as the mount type, or remove the extra orientations.";
    case "unattached":
      return isGreek
        ? `Ο προσανατολισμός «${name}» δεν έχει συνδεδεμένο MPPT. Συνδέστε τουλάχιστον ένα στον ηλεκτρολογικό σχεδιασμό ή αφαιρέστε τον προσανατολισμό.`
        : `“${name}” has no MPPT attached. Attach at least one in the Electrical design, or remove the orientation.`;
    case "unknownReference":
      return isGreek
        ? `Το MPPT ${problem.index} του ${problem.inverter} είναι συνδεδεμένο σε προσανατολισμό που δεν υπάρχει πλέον («${problem.tag}»). Συνδέστε το ξανά στον ηλεκτρολογικό σχεδιασμό.`
        : `MPPT ${problem.index} of ${problem.inverter} is attached to an orientation that no longer exists (“${problem.tag}”). Attach it again in the Electrical design.`;
    case "missingAngles":
      return isGreek
        ? `Ο προσανατολισμός «${name}» χρειάζεται κλίση και αζιμούθιο.`
        : `“${name}” needs both a tilt and an azimuth.`;
    case "tiltRange":
      return isGreek
        ? `Η κλίση του προσανατολισμού «${name}» πρέπει να είναι από 0° έως 90°.`
        : `The tilt of “${name}” must be between 0° and 90°.`;
    case "azimuthRange":
      return isGreek
        ? `Το αζιμούθιο του προσανατολισμού «${name}» πρέπει να είναι από 0° έως 360°, δεξιόστροφα από βορρά.`
        : `The azimuth of “${name}” must be between 0° and 360°, clockwise from north.`;
    case "duplicateId":
      return isGreek
        ? `Το αναγνωριστικό «${problem.id}» χρησιμοποιείται σε περισσότερους από έναν προσανατολισμούς.`
        : `The id “${problem.id}” is used by more than one orientation.`;
    case "badId":
    default:
      return isGreek
        ? `Το αναγνωριστικό «${problem.id}» δεν είναι έγκυρο: επιτρέπονται έως 32 γράμματα, ψηφία, «_» ή «-».`
        : `The id “${problem.id}” is not valid: up to 32 letters, digits, “_” or “-”.`;
  }
}

// ---------------------------------------------------------------- edits

/** The stored form of the list: the primary without a plane, the rest with one. */
function storedList(orientations) {
  return orientations.map((o, position) => {
    const entry = { id: o.id, name: o.autoName ? "" : o.name, autoName: Boolean(o.autoName), fieldType: FIELD_TYPE_FIXED };
    if (position === 0) return entry;
    const out = { ...entry, tilt: o.tilt, azim: o.azim };
    if (o.tiltReference === "ground") out.tiltReference = "ground";
    if (Object.prototype.hasOwnProperty.call(o, "roofPitch")) out.roofPitch = o.roofPitch;
    if (Object.prototype.hasOwnProperty.call(o, "roofAzimuth")) out.roofAzimuth = o.roofAzimuth;
    if (o.spacingInfo && typeof o.spacingInfo === "object") out.spacingInfo = o.spacingInfo;
    if (Array.isArray(o.parcel) && o.parcel.length) out.parcel = o.parcel;
    if (o.groundClearanceM !== null && o.groundClearanceM !== undefined) out.groundClearanceM = o.groundClearanceM;
    if (o.obstacleShading && typeof o.obstacleShading === "object") out.obstacleShading = o.obstacleShading;
    if (Array.isArray(o.sceneViews)) out.sceneViews = o.sceneViews;
    return out;
  });
}

function nextId(orientations) {
  const used = new Set(orientations.map((o) => o.id));
  let n = orientations.length + 1;
  while (used.has(`o${n}`)) n += 1;
  return `o${n}`;
}

/**
 * A further plane, starting as a copy of the primary's angles - PVsyst's new
 * orientation starts from the current one - so the designer edits what
 * differs rather than typing a plane from nothing.
 */
export function addOrientation(cfg) {
  const orientations = orientationsFromConfig(cfg);
  const primary = orientations[0];
  const lat = num(cfg?.loc_info?.lat);
  const plane = {
    id: nextId(orientations),
    name: "",
    autoName: true,
    fieldType: FIELD_TYPE_FIXED,
    tilt: primary.tilt ?? 20,
    azim: primary.azim ?? (lat !== null && lat < 0 ? 0 : 180),
    tiltReference: primary.tiltReference,
    ...(String(cfg?.panelOrientation?.installationType) === "roof_frame" ? {
      roofPitch: num(cfg?.panelOrientation?.roofPitch),
      roofAzimuth: num(cfg?.panelOrientation?.roofAzimuth),
    } : {}),
  };
  return { ...cfg, orientations: storedList([...orientations, plane]) };
}

/** Edit one plane. The primary's angles are panelOrientation's; only its name is edited here. */
export function updateOrientation(cfg, id, patch) {
  const orientations = orientationsFromConfig(cfg);
  if (orientations.length < 2) return cfg;
  const next = orientations.map((o, position) => {
    if (o.id !== id) return o;
    const allowed = position === 0
      ? { name: patch.name, autoName: patch.autoName }
      : patch;
    const merged = { ...o };
    Object.entries(allowed).forEach(([key, value]) => {
      if (value !== undefined) merged[key] = value;
    });
    if ("name" in allowed && allowed.name !== undefined) {
      const typed = String(allowed.name || "");
      merged.name = typed;
      merged.autoName = typed.trim() === "";
    }
    return merged;
  });
  return { ...cfg, orientations: storedList(next) };
}

/** One roof face owns its angles. The primary keeps its legacy roof fields. */
export function setFlushRoofFace(cfg, id, field, value) {
  if (field !== "tilt" && field !== "azim") return cfg;
  const orientations = orientationsFromConfig(cfg);
  const target = orientations.find((plane) => plane.id === id);
  if (!target) return cfg;
  if (!target.primary) return updateOrientation(cfg, id, { [field]: value });
  const panelOrientation = {
    ...(cfg.panelOrientation || {}),
    [field === "tilt" ? "roofPitch" : "roofAzimuth"]: value,
    [field]: value,
  };
  return { ...cfg, panelOrientation };
}

function retagMppts(entries, rename) {
  if (!Array.isArray(entries)) return entries;
  return entries.map((entry) => {
    if (!entry || typeof entry !== "object" || !("orientation" in entry)) return entry;
    const target = rename(entry.orientation);
    if (target === entry.orientation) return entry;
    const { orientation, ...rest } = entry;
    return target ? { ...rest, orientation: target } : rest;
  });
}

function retagDesign(design, rename) {
  if (!design || typeof design !== "object") return design;
  const out = { ...design };
  if (Array.isArray(design.layout)) out.layout = design.layout.map((inv) => retagMppts(inv, rename));
  if (design.layoutByInverter && typeof design.layoutByInverter === "object") {
    out.layoutByInverter = Object.fromEntries(
      Object.entries(design.layoutByInverter).map(([key, inv]) => [key, retagMppts(inv, rename)])
    );
  }
  return out;
}

function retagSystemDesign(systemDesign, rename) {
  if (!systemDesign || typeof systemDesign !== "object") return systemDesign;
  return Object.fromEntries(
    Object.entries(systemDesign).map(([key, entries]) => [key, retagMppts(entries, rename)])
  );
}

/** Rewrite every MPPT tag in the saved design, in systemDesign and pv_design alike. */
export function retagConfig(cfg, rename) {
  const out = { ...cfg };
  if (cfg?.systemDesign) out.systemDesign = retagSystemDesign(cfg.systemDesign, rename);
  if (cfg?.pv_design) out.pv_design = retagDesign(cfg.pv_design, rename);
  return out;
}

/**
 * Remove a further plane. Its MPPTs go back to the primary - the design keeps
 * its strings, they only stop standing on the plane that is gone - and the
 * count is returned so the screen can say so. Removing the last further plane
 * deletes the list and every tag: the project is single-plane again, stored
 * exactly as one that never had the feature.
 */
export function removeOrientation(cfg, id) {
  const orientations = orientationsFromConfig(cfg);
  const position = orientations.findIndex((o) => o.id === id);
  if (position <= 0) return { cfg, moved: 0 };
  const moved = mpptAssignments(cfg?.systemDesign, orientations).filter((a) => a.tag === id).length;
  const remaining = orientations.filter((o) => o.id !== id);
  if (remaining.length < 2) {
    const { orientations: _dropped, ...rest } = cfg;
    return { cfg: retagConfig(rest, () => null), moved };
  }
  const next = retagConfig({ ...cfg, orientations: storedList(remaining) }, (tag) => (tag === id ? null : tag));
  return { cfg: next, moved };
}

// ------------------------------------------------- roof faces as planes
//
// On a roof the planes ARE the faces that carry modules. A face drawn in the
// plan as "roof without PV" is an obstacle of the active plane's scene; giving
// it modules makes it a plane of its own, with the face's angles, and taking
// them away makes it an obstacle again. The list of orientations then follows
// the drawing instead of being kept beside it.

const DEFAULT_FACE_NAMES = new Set(["Πλευρά στέγης χωρίς Φ/Β", "Roof without PV", "Πολυγωνικό εμπόδιο", "Polygon obstacle"]);
const frameOf = (geometry) => ({
  eastM: num(geometry?.frame?.eastM) ?? 0,
  northM: num(geometry?.frame?.northM) ?? 0,
  elevationM: num(geometry?.frame?.elevationM) ?? 0,
});

/** The saved scene geometry of a plane, and a setter that writes it back. */
function planeGeometry(cfg, plane) {
  const spacing = plane.primary ? (cfg?.pv_design?.spacingInfo || null) : (plane.spacingInfo || null);
  return spacing?.site_geometry || null;
}
/**
 * A "roof without PV" face of the active plane's scene becomes a plane of its
 * own: a new orientation with the face's pitch and azimuth, carrying the face
 * as its roof, placed where the face stood. The face leaves the active
 * plane's obstacles (it is drawn as a plane from now on). `activeFrame` is the
 * active plane's frame as currently drawn, which may be ahead of the saved one.
 */
export function promoteRoofFace(cfg, activeId, obstacle, activeFrame = null, { installationType = "roof_flush" } = {}) {
  const planes = orientationsFromConfig(cfg);
  const active = planes.find((p) => p.id === activeId) || planes[0];
  if (!obstacle || obstacle.kind !== "roof") return { cfg, id: null };
  const saved = planeGeometry(cfg, active);
  const frame = activeFrame ? frameOf({ frame: activeFrame }) : frameOf(saved);
  const pitch = num(obstacle.pitchDeg) ?? 0;
  const azimuth = num(obstacle.rotationDeg) ?? 180;
  const typed = String(obstacle.name || "").trim();
  const name = DEFAULT_FACE_NAMES.has(typed) ? "" : typed;
  const roof = {
    ...(Array.isArray(obstacle.footprint) && obstacle.footprint.length ? { footprint: obstacle.footprint.map((p) => ({ ...p })) } : {}),
    widthM: obstacle.widthM, depthM: obstacle.depthM, heightM: obstacle.heightM,
    pitchDeg: pitch, azimuthDeg: azimuth, setbackM: 0.5,
    // The eave goes with the outline; its height is above the new frame's
    // ground, which stands at the face's base.
    ...(Number.isInteger(obstacle.eaveEdge) ? { eaveEdge: obstacle.eaveEdge } : {}),
    ...(num(obstacle.eaveHeightM) != null ? { eaveHeightM: num(obstacle.eaveHeightM) - (num(obstacle.baseHeightM) ?? 0) } : {}),
    ...(obstacle.freeAzimuth ? { freeAzimuth: true } : {}),
    ...(obstacle.azimuthSource ? { azimuthSource: obstacle.azimuthSource } : {}),
  };
  const geometry = {
    version: 2,
    frame: {
      eastM: frame.eastM + (num(obstacle.eastM) ?? 0),
      northM: frame.northM + (num(obstacle.northM) ?? 0),
      elevationM: frame.elevationM + (num(obstacle.baseHeightM) ?? 0),
    },
    roof, obstacles: [],
  };
  let next = addOrientation(cfg);
  const id = orientationsFromConfig(next).slice(-1)[0].id;
  const angles = installationType === "roof_frame"
    ? { roofPitch: pitch, roofAzimuth: azimuth, azim: azimuth }
    : { tilt: pitch, azim: azimuth, tiltReference: "horizontal" };
  next = updateOrientation(next, id, { ...angles, name, spacingInfo: { site_geometry: geometry } });
  // The active plane's SAVED scene is left alone: the face leaves the
  // editor's draft of it, and Save writes that. Rewriting the saved scene
  // here would reload the editor from the project mid-edit, bringing the
  // face back or dropping everything else drawn since the last Save.
  return { cfg: next, id };
}

/**
 * The reverse: a further plane's roof becomes a "roof without PV" face in the
 * active plane's scene, with its own obstacles, and the orientation goes -
 * its MPPTs return to the primary, as removeOrientation does. The primary and
 * the active plane cannot be demoted.
 */
export function demoteRoofFace(cfg, activeId, planeId, activeFrame = null, { installationType = "roof_flush", isGreek = false } = {}) {
  const planes = orientationsFromConfig(cfg);
  const active = planes.find((p) => p.id === activeId) || planes[0];
  const plane = planes.find((p) => p.id === planeId);
  if (!plane || plane.primary || plane.id === active.id) return { cfg, obstacle: null, carried: [], moved: 0 };
  const saved = planeGeometry(cfg, active);
  const frame = activeFrame ? frameOf({ frame: activeFrame }) : frameOf(saved);
  const geometry = planeGeometry(cfg, plane);
  let obstacle = null, carried = [];
  if (geometry?.roof) {
    const own = frameOf(geometry);
    const dx = own.eastM - frame.eastM, dy = own.northM - frame.northM, dz = own.elevationM - frame.elevationM;
    const pitch = installationType === "roof_frame" ? (num(plane.roofPitch) ?? num(geometry.roof.pitchDeg) ?? 0) : (num(plane.tilt) ?? 0);
    const azimuth = installationType === "roof_frame" ? (num(plane.roofAzimuth) ?? num(geometry.roof.azimuthDeg) ?? 180) : (num(plane.azim) ?? 180);
    obstacle = {
      id: `roof-${Date.now()}-${(saved?.obstacles || []).length}`,
      kind: "roof",
      name: !plane.autoName && plane.name ? plane.name : (isGreek ? "Πλευρά στέγης χωρίς Φ/Β" : "Roof without PV"),
      base: "street", eastM: dx, northM: dy, baseHeightM: dz,
      widthM: geometry.roof.widthM, depthM: geometry.roof.depthM, heightM: geometry.roof.heightM,
      pitchDeg: pitch, rotationDeg: azimuth,
      ...(Array.isArray(geometry.roof.footprint) && geometry.roof.footprint.length ? { footprint: geometry.roof.footprint.map((p) => ({ ...p })) } : {}),
      ...(Number.isInteger(geometry.roof.eaveEdge) ? { eaveEdge: geometry.roof.eaveEdge } : {}),
      ...(num(geometry.roof.eaveHeightM) != null ? { eaveHeightM: num(geometry.roof.eaveHeightM) + dz } : {}),
      ...(geometry.roof.freeAzimuth ? { freeAzimuth: true } : {}),
      ...(geometry.roof.azimuthSource ? { azimuthSource: geometry.roof.azimuthSource } : {}),
    };
    // Its own objects come along, re-based on the active plane's frame. The
    // face and they go into the editor's draft of the active plane (see
    // promoteRoofFace), not into its saved scene.
    carried = (geometry.obstacles || []).map((o) => ({
      ...o, id: `${o.id}-moved`, base: "street",
      eastM: (num(o.eastM) ?? 0) + dx, northM: (num(o.northM) ?? 0) + dy,
      baseHeightM: (num(o.baseHeightM) ?? 0) + dz + (o.base === "roof" ? (num(geometry.roof.heightM) ?? 0) : 0),
    }));
  }
  const removed = removeOrientation(cfg, planeId);
  return { cfg: removed.cfg, obstacle, carried, moved: removed.moved };
}

/**
 * The saved design with only the MPPTs standing on one plane, in systemDesign
 * shape - what that plane's layout places on its rows.
 *
 * Every inverter and every MPPT keeps its place; the ones on other planes are
 * left with no inputs. String ids are numbered by position
 * (pv_core.string_layout: INV-02-MPPT-03-IN-01), so a plane's layout, its
 * sheet and the single-line diagram then name each string the same way as the
 * whole plant does. Null when no MPPT stands on the plane.
 */
export function systemDesignForOrientation(systemDesign, orientations, orientationId) {
  const primaryId = orientations?.[0]?.id || PRIMARY_ID;
  if (!systemDesign || typeof systemDesign !== "object") return null;
  const out = {};
  let found = false;
  Object.entries(systemDesign).forEach(([inverter, entries]) => {
    out[inverter] = (Array.isArray(entries) ? entries : []).map((entry, position) => {
      if (!entry || typeof entry !== "object") return entry;
      const tag = entry.orientation == null || entry.orientation === "" ? primaryId : String(entry.orientation).trim();
      if (tag === orientationId) {
        found = true;
        return entry;
      }
      return { index: entry.index ?? position + 1, inputs: [] };
    });
  });
  return found ? out : null;
}

/** Set one MPPT's plane in a structured-design MPPT state (the dialog's shape). */
export function withMpptOrientation(mppt, orientationId, primaryId = PRIMARY_ID) {
  if (!mppt || typeof mppt !== "object") return mppt;
  const { orientation, ...rest } = mppt;
  return !orientationId || orientationId === primaryId ? rest : { ...rest, orientation: orientationId };
}

// ------------------------------------------------------ auto-design shares

/**
 * How the auto-designer's strings are shared out across the planes.
 *
 * The designer sizes strings for the plant as a whole - string voltages and
 * currents do not depend on the plane - so the only per-plane question is
 * how much of the plant stands on each. Shares are percentages of the target
 * kWp, kept on autoDesign.orientationShares by plane id; a plane with no
 * share stored gets an equal part of what is left.
 */
export function orientationShares(cfg) {
  const orientations = orientationsFromConfig(cfg);
  const stored = cfg?.autoDesign?.orientationShares;
  const given = stored && typeof stored === "object" ? stored : {};
  const known = orientations.filter((o) => num(given[o.id]) !== null);
  const knownTotal = known.reduce((sum, o) => sum + num(given[o.id]), 0);
  const open = orientations.filter((o) => num(given[o.id]) === null);
  const left = Math.max(0, 100 - knownTotal);
  // What is not stored is shared out by the room each plane has - its roof
  // face inside the edge setback - when every open plane has a face drawn;
  // otherwise in equal parts. A bigger face gets more of the plant.
  const areas = open.map((o) => usableRoofAreaM2(planeGeometry(cfg, o)?.roof));
  const byArea = areas.length > 0 && areas.every((a) => a !== null) && areas.some((a) => a > 0);
  const areaTotal = byArea ? areas.reduce((sum, a) => sum + a, 0) : 0;
  const defaults = {};
  let handed = 0;
  open.forEach((o, i) => {
    const raw = byArea ? (areaTotal > 0 ? left * areas[i] / areaTotal : 0) : (open.length ? left / open.length : 0);
    const share = i === open.length - 1 ? Math.round((left - handed) * 10) / 10 : Math.round(raw * 10) / 10;
    handed += share;
    defaults[o.id] = Math.max(0, share);
  });
  return orientations.map((o) => ({
    id: o.id,
    share: num(given[o.id]) !== null ? num(given[o.id]) : defaults[o.id],
    stored: num(given[o.id]) !== null,
  }));
}

/** The area of a roof face inside its setback, in m²; null without a face. */
export function usableRoofAreaM2(roof) {
  if (!roof) return null;
  const ring = Array.isArray(roof.footprint) && roof.footprint.length >= 3
    ? roof.footprint
    : (num(roof.widthM) > 0 && num(roof.depthM) > 0
      ? [{ x: 0, y: 0 }, { x: num(roof.widthM), y: 0 }, { x: num(roof.widthM), y: num(roof.depthM) }, { x: 0, y: num(roof.depthM) }]
      : null);
  if (!ring) return null;
  let twice = 0, perimeter = 0;
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length];
    twice += a.x * b.y - b.x * a.y;
    perimeter += Math.hypot(b.x - a.x, b.y - a.y);
  });
  const area = Math.abs(twice) / 2;
  const setback = Math.max(0, num(roof.setbackM) ?? 0);
  // The strip along the edges, less the corners counted twice.
  return Math.max(0, area - setback * perimeter + 4 * setback * setback);
}

/** The shares' total, and whether it is the 100 % a design can be built to. */
export function sharesTotal(shares) {
  const total = (shares || []).reduce((sum, s) => sum + (num(s.share) || 0), 0);
  return { total, valid: Math.abs(total - 100) < 0.05 && (shares || []).every((s) => (num(s.share) || 0) >= 0) };
}

const mpptStateModules = (mppt) => {
  const mps = num(mppt?.modulesPerString) || 0;
  const used = (Array.isArray(mppt?.inputs) ? mppt.inputs : []).filter((i) => i?.used).length;
  return mps > 0 ? mps * used : 0;
};

/**
 * Tag the MPPTs of a structured design (the dialog's shape: layout = [[mppt,
 * ...], ...] per inverter, layoutByInverter alike) so each plane holds its
 * share of the modules.
 *
 * Whole inverters first: an inverter goes to the plane furthest below its
 * share, MPPT by MPPT, and only the inverter that crosses a plane's share is
 * split between two planes. That keeps each plane's strings on as few
 * inverters as the shares allow, which is how the plant is wired in practice.
 * The primary's MPPTs carry no tag; a further plane's carry its id.
 */
export function distributeDesignAcrossOrientations(design, orientations, shares) {
  if (!design || typeof design !== "object") return design;
  const planes = (orientations || []).map((o) => o.id);
  if (planes.length < 2) return design;
  const primaryId = planes[0];
  const shareOf = Object.fromEntries((shares || []).map((s) => [s.id, Math.max(0, num(s.share) || 0)]));
  const layout = Array.isArray(design.layout)
    ? design.layout
    : design.layoutByInverter
      ? (design.inverterOrder || Object.keys(design.layoutByInverter)).map((k) => design.layoutByInverter[k] || [])
      : null;
  if (!layout) return design;

  const total = layout.reduce((sum, inv) => sum + (inv || []).reduce((s, m) => s + mpptStateModules(m), 0), 0);
  const deficit = Object.fromEntries(planes.map((id) => [id, (total * (shareOf[id] || 0)) / 100]));
  const pick = () => planes.reduce((best, id) => (deficit[id] > deficit[best] ? id : best), planes[0]);

  // The plane being filled carries on from one inverter to the next until it
  // has its share; only then is the next plane chosen, by deficit. So the
  // planes come out as contiguous runs of inverters, split at one point.
  let current = null;
  const tagged = layout.map((inv) =>
    (inv || []).map((mppt) => {
      const modules = mpptStateModules(mppt);
      if (modules <= 0) return withMpptOrientation(mppt, null, primaryId);
      if (current === null || deficit[current] <= 1e-9) current = pick();
      deficit[current] -= modules;
      return withMpptOrientation(mppt, current, primaryId);
    })
  );

  const out = { ...design, layout: tagged };
  if (design.layoutByInverter) {
    const order = design.inverterOrder || Object.keys(design.layoutByInverter);
    out.layoutByInverter = Object.fromEntries(order.map((k, i) => [k, tagged[i] || []]));
  }
  return out;
}
